import { useCallback, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { CellDoubleClickedEvent } from 'ag-grid-enterprise'
import { isWeekend } from 'date-fns'
import { Trash } from 'lucide-react'
import { DateRange } from 'react-day-picker'

import { getDatesInDayRange, getT, getTMinus, toISO8601 } from '@fortem-monorepo/js-utilities'
import { getBasicColDefsFromData } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@fortem-monorepo/react-ui'
import { Feeds } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { DeleteProviderTaskDataModal } from '@/components/dialogs/DeleteProviderTaskDataModal'
import { RunManualTaskModal } from '@/components/dialogs/RunManualTaskModal'
import { FEEDS } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { createCols } from '@/pages/feeds/providers/create-columns'
import {
  feedsDataOptions,
  feedsNTOptions,
  feedsProvidersOptions,
} from '@/react-query/queries/feeds'

export interface FeedsProvidersPageProps {}

export const FeedsProvidersPage: React.FC<FeedsProvidersPageProps> = ({}) => {
  const api = useApi()
  const [selectedDate, setDate] = useState<DateRange>({
    from: getTMinus(5),
    to: getT(),
  })

  const [selectedFeed, setSelectedFeed] = useState<{
    provider_id: string
    feed_id: string
    st_dt: string
    ed_dt?: string
    fund_id?: string
  } | null>(null)

  const onFeedDoubleClicked = (e: CellDoubleClickedEvent<Feeds.FeedData>) => {
    const date = e.column.getUserProvidedColDef()!.field

    const provider_id = e.data?.provider_id
    const feed_id = e.data?.feed_id
    const fund_id = e.data?.fund_id

    if (date && provider_id && feed_id) {
      setSelectedFeed({
        provider_id,
        feed_id,
        st_dt: date,
        fund_id,
      })
    }
  }

  const dataQuery = useQuery({
    ...feedsDataOptions(api, selectedFeed!),
    enabled: !!selectedFeed?.provider_id && !!selectedFeed.feed_id && !!selectedFeed.st_dt,
  })

  const specificData = useMemo(() => {
    return dataQuery.data ?? []
  }, [dataQuery.data])

  const getDates = useCallback(() => {
    return getDatesInDayRange(
      new Date(selectedDate.from!),
      selectedDate.to ? new Date(selectedDate.to) : new Date(selectedDate.from!),
      true,
    )
  }, [selectedDate.to, selectedDate.from])

  const ntDefs = useMemo(() => {
    return createCols(getDates(), true, onFeedDoubleClicked)
  }, [selectedDate.from, selectedDate.to])

  const bankDefs = useMemo(() => {
    return createCols(getDates(), false, onFeedDoubleClicked)
  }, [selectedDate.from, selectedDate.to])

  const dates = {
    st_dt: toISO8601(selectedDate.from!),
    ed_dt: toISO8601(selectedDate.to ?? selectedDate.from!),
  }

  const allBanksQuery = useQuery(feedsProvidersOptions(api, dates))
  const ntQuery = useQuery(feedsNTOptions(api, dates))

  const [runTaskOpen, setRunTaskOpen] = useState(false)
  const [deleteTaskDataOpen, setDeleteTaskDataOpen] = useState(false)
  const [tab, setTab] = useState<'funds' | 'other'>('funds')

  return (
    <Page
      metadata={{ title: 'Agents - Feeds' }}
      permissions={FEEDS.PROVIDERS.permissions}
      layout={'normal'}
    >
      <RunManualTaskModal open={runTaskOpen} onOpenChange={setRunTaskOpen} />
      <DeleteProviderTaskDataModal
        open={deleteTaskDataOpen}
        onOpenChange={setDeleteTaskDataOpen}
        dates={dates}
      />
      <Tabs
        value={tab}
        onValueChange={(val) => setTab(val as typeof tab)}
        className={'h-full gap-0'}
      >
        <HeaderConfigBar>
          <div className={'flex items-center gap-2'}>
            <DateRangePicker
              size="sm"
              date={selectedDate}
              disabled={(date) => date > new Date() || isWeekend(date)}
              setDate={(val) => val && setDate(val)}
            />
            <TabsList>
              <TabsTrigger value="funds">Funds</TabsTrigger>
              <TabsTrigger value="other">Other</TabsTrigger>
            </TabsList>
            <CanAccess permissions={[PERMISSIONS.RUN_DATA_PRESENCE]}>
              {/* <Button size="sm" onClick={() => setRunTaskOpen(true)} variant={"outline"}>
              Run task <Play />
            </Button> */}
              <Button size="sm" onClick={() => setDeleteTaskDataOpen(true)} variant="outline">
                Delete data <Trash />
              </Button>
            </CanAccess>
          </div>
        </HeaderConfigBar>
        <ResizablePanelGroup direction={'horizontal'}>
          <ResizablePanel id="feeds-left-panel" order={1}>
            <TabsContent value="funds" className={'h-full'}>
              <Widget exposed className={'h-full'}>
                <WidgetContent loading={ntQuery?.isLoading}>
                  <WidgetHeaderWithQuery query={ntQuery} title="Northern Trust" />
                  <CustomAgGrid
                    columnDefs={ntDefs}
                    rowData={ntQuery.data}
                    autoSizeStrategy={{ type: 'fitCellContents' }}
                    groupDefaultExpanded={-1}
                    domLayout={'normal'}
                    statusBar={{ statusPanels: [] }}
                    groupAllowUnbalanced={true}
                    autoGroupColumnDef={{
                      width: 210,
                      suppressSizeToFit: true,
                    }}
                  />
                </WidgetContent>
              </Widget>
            </TabsContent>
            <TabsContent value="other" className={'h-full'}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery query={allBanksQuery} title="Others" />
                <WidgetContent loading={allBanksQuery?.isLoading}>
                  <CustomAgGrid
                    columnDefs={bankDefs}
                    rowData={allBanksQuery.data}
                    autoSizeStrategy={{ type: 'fitCellContents' }}
                    domLayout={'normal'}
                    groupAllowUnbalanced={true}
                    statusBar={{ statusPanels: [] }}
                    autoGroupColumnDef={{
                      resizable: false,
                      suppressSizeToFit: true,
                    }}
                  />
                </WidgetContent>
              </Widget>
            </TabsContent>
          </ResizablePanel>
          {(dataQuery.isFetching || specificData.length > 0) && (
            <>
              <ResizableHandle />
              <ResizablePanel id={`specific-data`} order={2}>
                <Widget exposed className={'h-full'}>
                  <WidgetHeaderWithQuery
                    query={dataQuery}
                    title={`${selectedFeed?.provider_id.toUpperCase()} ${selectedFeed?.feed_id.toUpperCase()} ${selectedFeed?.st_dt}`}
                  />
                  <WidgetContent>
                    <CustomAgGrid
                      domLayout={'normal'}
                      rowData={specificData}
                      columnDefs={getBasicColDefsFromData(specificData)}
                    />
                  </WidgetContent>
                </Widget>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </Tabs>
    </Page>
  )
}
